import { FC } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Calendar, MapPin, Package, Pencil, Trash2, PackageCheck, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ContainerStats } from './ContainerStats';
import { BatteryTypeSection } from './BatteryTypeSection';
import { Badge } from '@/components/ui/badge';
import type { Container, Battery } from '@/hooks/useBatteryContainers';

interface BatteryContainer3DProps {
  container: Container;
  batteries: Battery[];
  allocations?: Map<string, string>;
  onBatteryDrop?: (batteryId: string, leadId: string) => void;
  onAddBatteries?: () => void;
  onEditContainer?: () => void;
  onDeleteContainer?: () => void;
  onEditBattery?: (battery: Battery) => void;
  onDeleteBattery?: (battery: Battery) => void;
  onAllocateBattery?: (battery: Battery) => void;
  onBookToStock?: () => void;
}

export const BatteryContainer3D: FC<BatteryContainer3DProps> = ({
  container,
  batteries,
  allocations,
  onBatteryDrop,
  onEditContainer,
  onDeleteContainer,
  onEditBattery,
  onDeleteBattery,
  onAllocateBattery,
  onBookToStock,
}) => {
  // Filter batteries for this container and sort by creation date
  const containerBatteries = batteries
    .filter((b) => b.container_id === container.id)
    .sort((a, b) => {
      // Sort 50kW first, then 100kW
      if (a.power_kw !== b.power_kw) {
        return a.power_kw - b.power_kw;
      }
      return 0;
    });

  // Split by battery type
  const batteries50kw = containerBatteries.filter((b) => b.power_kw === 50);
  const batteries100kw = containerBatteries.filter((b) => b.power_kw === 100);

  const max50kw = container.max_batteries_50kw || 16;
  const max100kw = container.max_batteries_100kw || 8;

  return (
    <Card className={`w-96 flex-shrink-0 ${container.status === 'aangekomen' ? 'border-l-4 border-l-green-500' : ''}`}>
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div className="space-y-1 flex-1">
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg">{container.name}</CardTitle>
              {container.status === 'aangekomen' && (
                <Badge variant="default" className="bg-green-500 hover:bg-green-600">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Aangekomen
                </Badge>
              )}
            </div>
            <div className="flex flex-col gap-1 text-xs text-muted-foreground">
              {container.location && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {container.location}
                </div>
              )}
              {container.arrival_date && (
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {new Date(container.arrival_date).toLocaleDateString('nl-NL')}
                </div>
              )}
              <div className="flex items-center gap-1">
                <Package className="h-3 w-3" />
                Status: {container.status}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {onEditContainer && (
              <Button size="sm" variant="ghost" onClick={onEditContainer}>
                <Pencil className="h-4 w-4" />
              </Button>
            )}
            {onDeleteContainer && (
              <Button size="sm" variant="ghost" onClick={onDeleteContainer}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {container.status === 'onderweg' && onBookToStock && (
          <Button 
            size="sm" 
            variant="default" 
            className="w-full mt-3 bg-green-600 hover:bg-green-700"
            onClick={onBookToStock}
          >
            <PackageCheck className="h-4 w-4 mr-2" />
            Boek in voorraad
          </Button>
        )}

        <ContainerStats
          current50kw={batteries50kw.length}
          max50kw={max50kw}
          current100kw={batteries100kw.length}
          max100kw={max100kw}
        />
      </CardHeader>

      <CardContent className="space-y-4">
        <BatteryTypeSection
          title="50kW Batterijen"
          batteries={batteries50kw}
          maxBatteries={max50kw}
          size="single"
          allocations={allocations}
          onAllocate={onAllocateBattery}
          onEdit={onEditBattery}
          onDelete={onDeleteBattery}
        />

        <BatteryTypeSection
          title="100kW Batterijen"
          batteries={batteries100kw}
          maxBatteries={max100kw}
          size="double"
          allocations={allocations}
          onAllocate={onAllocateBattery}
          onEdit={onEditBattery}
          onDelete={onDeleteBattery}
        />

        {container.notes && (
          <div className="text-xs text-muted-foreground pt-2 border-t">
            <p className="font-medium mb-1">Notities:</p>
            <p className="whitespace-pre-wrap">{container.notes}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
